import filter from 'lodash/filter';
import _differenceBy from 'lodash/differenceBy';
import _uniq from 'lodash/uniq';
import { getRetailPrice, mapBopsAvailability } from 'utils';
import { getAdjustedPrice } from 'modules/clients/utils';
import actionTypes from '../actions/actionTypes';
import { mapProduct } from './utils';

const INITIAL_STATE = {
  conciergeProducts: [],
  customerProducts: [],
  appliedPromoCodes: [],
  saveForLater: [],
  saveForLaterFarfetch: [],
  saveForLaterLoading: false,
  saveForLaterFarfetchLoading: false,
  loading: false,
  conciergeProductsLoading: false,
  conciergeFarfetchProductsLoading: false,
  customerProductsLoading: false,
  itemskuId: '',
  isGettingProduct: false,
  gettingProductError: '',
  productToAdd: {},
  error: null,
  farfetchConciergeProducts: [],
  farfetchError: null,
};

export const mapProductsData = (item, products, isCustomerProducts) => {
  const idType = isCustomerProducts ? 'webProductSku' : 'itemskuId';
  const existingProduct = products.find((p) => p[idType] === item[idType]);
  const filteredProducts = products.filter((p) => p[idType] !== item[idType]);
  const bopsItem = mapBopsAvailability(item);

  if (existingProduct) {
    existingProduct.quantity += item.quantity || 1;
  }
  return [existingProduct || mapProduct(bopsItem), ...filteredProducts];
};

export const mapSflProducts = (item, products) => {
  let isProductExists = false;

  const existingProducts = products.map((p) => {
    if (p.itemskuId === item.itemskuId) {
      isProductExists = true;
      return {
        ...p,
        quantity: p.quantity + item.quantity,
      };
    }
    return p;
  });

  return isProductExists ? existingProducts : [...products, item];
};

const mapOrderReviewData = (product, payload) => {
  const promotionDiscAmt = `${payload.promotionDiscAmt / product.quantity}`;
  return {
    ...product,
    promotionDiscAmt,
    promotionsApplied: payload.promotionsApplied || [],
    price: {
      ...product.price,
      itemPriceEach: payload.itemPriceEach,
      itemPriceExt: payload.itemPriceExt,
      itemTax: payload.itemTax,
      retailPrice: getRetailPrice(payload.itemPriceEach, promotionDiscAmt),
    },
  };
};

const mapPriceAdjustmentData = (product, { itemskuId, ...payload }) => {
  return {
    ...product,
    price: {
      ...product.price,
      ...payload,
      adjustedPromoPrice: getAdjustedPrice(product.price.promoPrice, payload.adjustmentValue, payload.priceType),
    },
  };
};

const mapGiftOptionsData = (product, payload) => ({
  ...product,
  giftOptions: {
    ...product.giftOptions,
    giftWrapCode: payload.giftOptions.giftWrapCode,
    giftCard: payload.giftOptions.giftCard,
    giftMessage1: payload.giftOptions.giftMessage1 || '',
    giftMessage2: payload.giftOptions.giftMessage2 || '',
    giftMessage3: payload.giftOptions.giftMessage3 || '',
    giftMessage4: payload.giftOptions.giftMessage4 || '',
    giftMessage5: payload.giftOptions.giftMessage5 || '',
    giftReceipt: payload.giftOptions.giftReceipt || false,
  },
});

export const conciergeCart = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case actionTypes.CONCIERGE_CART_PRODUCTS_SUCCESS:
      return {
        ...state,
        conciergeProductsLoading: false,
        conciergeFarfetchProductsLoading: false,
        conciergeProducts: payload.map((product) => mapProduct(product)),
      };

    case actionTypes.CUSTOMER_CART_PRODUCTS_SUCCESS:
      return {
        ...state,
        customerProductsLoading: false,
        customerProducts: payload.map((product) => mapProduct(product)),
      };

    case actionTypes.CONCIERGE_CART_PRODUCTS_LOADING:
      return {
        ...state,
        conciergeProducts: [],
        conciergeProductsLoading: true,
        error: null,
        farfetchError: null,
        gettingProductError: '',
      };
    case actionTypes.CONCIERGE_FARFETCH_CART_PRODUCTS_LOADING:
      return {
        ...state,
        conciergeFarfetchProductsLoading: true,
        error: null,
        farfetchError: null,
        farfetchConciergeProducts: [],
        gettingProductError: '',
      };

    case actionTypes.CONCIERGE_FARFETCH_CART_PRODUCTS_SUCCESS:
      return {
        ...state,
        conciergeFarfetchProductsLoading: false,
        farfetchConciergeProducts: payload.map((product) => mapProduct(product)),
      };

    case actionTypes.CONCIERGE_FARFETCH_CART_PRODUCTS_ERROR:
      return {
        ...state,
        conciergeFarfetchProductsLoading: false,
        farfetchConciergeProducts: [],
        farfetchError: payload,
      };

    case actionTypes.CUSTOMER_CART_PRODUCTS_LOADING:
      return {
        ...state,
        customerProducts: [],
        customerProductsLoading: true,
        error: null,
      };

    case actionTypes.CONCIERGE_CART_PRODUCTS_ERROR:
      return {
        ...state,
        conciergeProductsLoading: false,
        conciergeFarfetchProductsLoading: false,
        error: payload,
      };

    case actionTypes.CUSTOMER_CART_PRODUCTS_ERROR:
      return {
        ...state,
        customerProductsLoading: false,
        error: payload,
      };

    case actionTypes.GET_PRODUCT_BY_SKU_LOADING:
      return {
        ...state,
        isGettingProduct: true,
        gettingProductError: '',
        productToAdd: {},
      };

    case actionTypes.GET_PRODUCT_BY_SKU_SUCCESS:
      return {
        ...state,
        isGettingProduct: false,
        gettingProductError: '',
        itemskuId: '',
        productToAdd: payload,
      };

    case actionTypes.GET_PRODUCT_BY_SKU_ERROR:
      return {
        ...state,
        gettingProductError: payload,
        itemskuId: '',
        isGettingProduct: false,
        productToAdd: {},
      };

    case actionTypes.CHANGE_SKU:
      return {
        ...state,
        itemskuId: payload,
        gettingProductError: '',
      };

    case actionTypes.ADD_PRODUCT_TO_CONCIERGE_CART_SUCCESS:
      return {
        ...state,
        conciergeProducts: mapProductsData(payload, state.conciergeProducts),
        productToAdd: {},
      };
    case actionTypes.ADD_PRODUCT_TO_FARFETCH_CONCIERGE_CART_SUCCESS:
      return {
        ...state,
        conciergeProducts: mapProductsData(payload, state.conciergeProducts),
        farfetchConciergeProducts: mapProductsData(payload, state.farfetchConciergeProducts),
        productToAdd: {},
      };
    case actionTypes.ADD_PRODUCT_TO_CUSTOMER_CART_SUCCESS:
      return {
        ...state,
        customerProducts: mapProductsData(payload, state.customerProducts, true),
      };

    case actionTypes.REMOVE_ITEM_FROM_CONCIERGE_CART_SUCCESS:
      return {
        ...state,
        conciergeProducts: filter(state.conciergeProducts, (p) => p.itemskuId !== payload),
      };
    case actionTypes.REMOVE_FARFETCH_ITEM_FROM_CONCIERGE_CART_SUCCESS:
      return {
        ...state,
        conciergeProducts: filter(state.conciergeProducts, (p) => p.itemskuId !== payload),
        farfetchConciergeProducts: filter(state.farfetchConciergeProducts, (p) => p.itemskuId !== payload),
      };
    case actionTypes.REMOVE_ITEM_FROM_CUSTOMER_CART_SUCCESS:
      return {
        ...state,
        customerProducts: filter(state.customerProducts, (p) => p.webProductSku !== payload),
      };

    case actionTypes.CHANGE_CONCIERGE_CART_ITEM_LOADING:
      return {
        ...state,
        conciergeProducts: state.conciergeProducts.map((p) =>
          p.itemskuId === payload.itemskuId ? { ...p, isLoading: true } : p,
        ),
      };

    case actionTypes.CHANGE_CONCIERGE_CART_ITEM_ERROR:
      return {
        ...state,
        conciergeProducts: state.conciergeProducts.map((p) =>
          p.itemskuId === payload.itemskuId ? { ...p, isLoading: false } : p,
        ),
      };

    case actionTypes.CHANGE_CONCIERGE_CART_ITEM_SUCCESS:
      return {
        ...state,
        conciergeProducts: state.conciergeProducts.map((p) =>
          p.itemskuId === payload.itemskuId ? { ...payload, isLoading: false } : p,
        ),
      };
    case actionTypes.CHANGE_FARFETCH_CONCIERGE_CART_ITEM_SUCCESS:
      return {
        ...state,
        farfetchConciergeProducts: state.farfetchConciergeProducts.map((p) =>
          p.itemskuId === payload.itemskuId ? { ...payload, isLoading: false } : p,
        ),
      };

    case actionTypes.CHANGE_CUSTOMER_CART_ITEM_SUCCESS:
      return {
        ...state,
        customerProducts: state.customerProducts.map((p) =>
          p.webProductSku === payload.webProductSku ? { ...payload, isLoading: false } : p,
        ),
      };

    case actionTypes.ORDER_REVIEW_SUCCESS:
      return {
        ...state,

        conciergeProducts: state.conciergeProducts.map((product) => {
          const methodItem = payload.shipmentOptions.reduce((p, method) => {
            return p || method.items.find((item) => item.cmosSku === product.cmosskuId);
          }, null);

          return !methodItem ? { ...product } : mapOrderReviewData(product, methodItem);
        }),
      };

    case actionTypes.APPLY_PROMO:
      return {
        ...state,
        appliedPromoCodes: _uniq([...state.appliedPromoCodes, ...payload]),
      };

    case actionTypes.PRODUCT_PRICE_ADJUSTMENT_LOADING:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.PRODUCT_PRICE_ADJUSTMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        conciergeProducts: state.conciergeProducts.map((product) => {
          return product.itemskuId !== payload.itemskuId ? { ...product } : mapPriceAdjustmentData(product, payload);
        }),
      };

    case actionTypes.PRODUCT_PRICE_ADJUSTMENT_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };

    case actionTypes.GIFT_OPTIONS_LOADING:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.GIFT_OPTIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        conciergeProducts: state.conciergeProducts.map((product) => {
          return product.itemskuId !== payload.itemskuId ? { ...product } : mapGiftOptionsData(product, payload);
        }),
      };

    case actionTypes.GIFT_OPTIONS_ERROR:
    case actionTypes.MOVE_ITEM_FROM_SFL_TO_CONCIERGE_ERROR:
    case actionTypes.MOVE_ONLINE_BAG_PRODUCTS_TO_CONCIERGE_ERROR:
    case actionTypes.ADD_ITEM_TO_SAVE_FOR_LATER_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };

    case actionTypes.SAVE_FOR_LATER_PRODUCTS_SUCCESS:
      return {
        ...state,
        saveForLater: payload.map((product) => mapProduct(product)),
        saveForLaterLoading: false,
        saveForLaterFarfetchLoading: false,
      };

    case actionTypes.SAVE_FOR_LATER_PRODUCTS_LOADING:
      return {
        ...state,
        saveForLaterLoading: false,
        saveForLaterFarfetchLoading: true,
        saveForLaterFarfetch: [],
      };

    case actionTypes.SAVE_FOR_LATER_PRODUCTS_ERROR:
      return {
        ...state,
        saveForLaterLoading: false,
        saveForLaterFarfetchLoading: false,
      };
    case actionTypes.SAVE_FOR_LATER_FARFETCH_PRODUCTS_SUCCESS:
      return {
        ...state,
        saveForLaterFarfetch: payload.map((product) => mapProduct(product)),
        saveForLaterLoading: false,
        saveForLaterFarfetchLoading: false,
      };
    case actionTypes.SAVE_FOR_LATER_FARFETCH_PRODUCTS_ERROR:
      return {
        ...state,
        saveForLaterLoading: false,
        saveForLaterFarfetchLoading: false,
      };
    case actionTypes.SAVE_FOR_LATER_FARFETCH_PRODUCTS_RESET:
      return {
        ...state,
        saveForLaterLoading: false,
        saveForLaterFarfetchLoading: false,
        saveForLaterFarfetch: [],
      };
    case actionTypes.ADD_ITEM_TO_SAVE_FOR_LATER_LOADING:
    case actionTypes.REMOVE_ITEM_FROM_SAVE_FOR_LATER_LOADING:
    case actionTypes.MOVE_ITEM_FROM_SFL_TO_CONCIERGE_LOADING:
    case actionTypes.MOVE_ONLINE_BAG_PRODUCTS_TO_CONCIERGE_LOADING:
      return {
        ...state,
        loading: true,
      };

    case actionTypes.ADD_ITEM_TO_SAVE_FOR_LATER_SUCCESS:
      return {
        ...state,
        conciergeProducts: filter(state.conciergeProducts, (p) => p.itemskuId !== payload.itemskuId),
        saveForLater: mapSflProducts(payload, state.saveForLater),
        loading: false,
      };
    case actionTypes.ADD_FARFETCH_ITEM_TO_SAVE_FOR_LATER_SUCCESS:
      return {
        ...state,
        conciergeProducts: filter(state.conciergeProducts, (p) => p.itemskuId !== payload.itemskuId),
        farfetchConciergeProducts: filter(state.farfetchConciergeProducts, (p) => p.itemskuId !== payload.itemskuId),
        saveForLater: mapSflProducts(payload, state.saveForLater),
        saveForLaterFarfetch: mapSflProducts(payload, state.saveForLaterFarfetch),
        loading: false,
      };
    case actionTypes.MOVE_BATCH_ITEMS_TO_SAVE_FOR_LATER_SUCCESS:
      return {
        ...state,
        conciergeProducts: _differenceBy(state.conciergeProducts, payload, 'itemskuId'),
        saveForLater: payload.map((item) => mapSflProducts(item, state.saveForLater)),
        loading: false,
      };
    case actionTypes.REMOVE_ITEM_FROM_SAVE_FOR_LATER_SUCCESS:
      return {
        ...state,
        saveForLater: filter(state.saveForLater, (p) => p.itemskuId !== payload.itemskuId),
        loading: false,
      };
    case actionTypes.REMOVE_FARFETCH_ITEM_FROM_SAVE_FOR_LATER_SUCCESS:
      return {
        ...state,
        saveForLater: filter(state.saveForLater, (p) => p.itemskuId !== payload.itemskuId),
        saveForLaterFarfetch: filter(state.saveForLaterFarfetch, (p) => p.itemskuId !== payload.itemskuId),
        loading: false,
      };
    case actionTypes.MOVE_ITEM_FROM_SFL_TO_CONCIERGE_SUCCESS:
      return {
        ...state,
        saveForLater: filter(state.saveForLater, (p) => p.itemskuId !== payload.itemskuId),
        conciergeProducts: mapSflProducts(payload, state.conciergeProducts),
        loading: false,
      };
    case actionTypes.MOVE_FARFETCH_ITEM_FROM_SFL_TO_CONCIERGE_SUCCESS:
      return {
        ...state,
        saveForLater: filter(state.saveForLater, (p) => p.itemskuId !== payload.itemskuId),
        saveForLaterFarfetch: filter(state.saveForLaterFarfetch, (p) => p.itemskuId !== payload.itemskuId),
        conciergeProducts: mapSflProducts(payload, state.conciergeProducts),
        farfetchConciergeProducts: mapSflProducts(payload, state.farfetchConciergeProducts),
        loading: false,
      };
    case actionTypes.MOVE_ONLINE_BAG_PRODUCTS_TO_CONCIERGE_SUCCESS: {
      // TODO: Disabled filtering of "moved" item according to OSP-7901 requirements
      // TODO: It should be recovered as soon as BE will be capable to remove item from Concierge cart on "Move to Concierge Bag" operation
      // TODO: So, left commented code  below for future usage
      //const ids = payload.map(({ itemId }) => itemId);
      return {
        ...state,
        //customerProducts: state.customerProducts.filter((p) => !ids.includes(p.itemskuId)),
        loading: false,
      };
    }

    default:
      return state;
  }
};

export default conciergeCart;
